<template>
  <!--    Society Form-->
  <validation-observer
      ref="organizationForm"
      v-slot="{invalid}"
  >
    <am-modal
        :title="`${updateOrganizationMode?'Update':'Create'} Society`"
        :show="organizationModalOpened"
        :show-overlay="deletingOrganization"
        size="lg"
        @onClose="closeOrganizationModal()"
    >
      <b-row class="align-items-start">
        <!-- Society Selection/Deletion Controls -->
        <div class="col-7">
          <am-select
              v-model="selectedOrganizationId"
              placeholder="All Societies"
              :options="organizations"
              label-name="name"
              value-name="id"
              name="organizations"
              @input="populateOrganizationForm"
          />
        </div>
        <div class="col-2">
          <am-dropdown
              icon="TrashIcon"
              name="delete_organization"
          >
            <b-dropdown-item
                id="cd_organization"
                @click="deleteOrganization()"
            >
              Yes
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </am-dropdown>
        </div>
        <div class="col-2">
          <am-button
              name="organization_action"
              variant="flat-danger"
              icon="XIcon"
              label=""
              tool-tip-text="Clear Form"
              @click="resetOrganizationForm()"
          />
        </div>
      </b-row>
      <error-display :error="error"/>
      <app-collapse
          accordion
          type="margin"
      >
        <app-collapse-item
            id="organization-details"
            title="Society Details"
        >
          <div class="row">
            <div class="col-md-6">
              <am-input
                  v-model="organization.name"
                  name="name"
                  label="Name"
                  rules="required"
              />
            </div>
            <div class="col-md-6">
              <am-date-picker
                  id="date_establishment"
                  v-model="organization.date_established"
                  name="date_established"
                  label="Date Established"
                  rules="required|no_future_date"
              />
            </div>
            <div class="col-md-6">
              <am-input
                  v-model="organization.registration_no"
                  name="registration_no"
                  label="Registration Number"
                  rules="required"
              />
            </div>
            <!-- Membership Size REMOVED -->
            <div class="col-md-6">
              <am-date-picker
                  id="date_joined_union"
                  v-model="organization.date_joined_union"
                  name="date_joined_union"
                  label="Date Joined Union"
                  rules="required|no_future_date"
              />
            </div>
            <div class="col-md-6">
              <am-select
                  v-model="organization.join_reason_id"
                  name="join_reason"
                  label="Join Reason"
                  :rules="''"
                  label-name="name"
                  value-name="id"
                  :options="joinReasons"
                  placeholder="Select Join Reason"
              />
            </div>
            <!-- Sustainability Certification Dropdown -->
            <div class="col-md-6">
              <am-select
                  v-model="organization.sustainability_certification"
                  name="sustainability_certification"
                  label="Sustainability Certification"
                  :rules="''"
                  :options="sustainabilityCertificationOptions"
                  placeholder="Select Certification"
              />
            </div>
            <!-- Conditional Input for 'Other' Sustainability Certification -->
            <div class="col-md-6" v-if="showOtherSustainabilityInput">
              <am-input
                  v-model="organization.other_sustainability_certification"
                  name="other_sustainability_certification"
                  label="Specify Other Certification"
                  rules="required"
              />
            </div>
            <!-- Annual Revenue -->
            <div class="col-md-6">
              <am-select
                  v-model="organization.annual_revenue"
                  name="annual_revenue"
                  label="Annual Revenue"
                  rules="required"
                  :options="annualRevenueOptions"
                  placeholder="Select Annual Revenue"
              />
            </div>
            <div class="col-md-6">
              <am-input
                  v-model="organization.no_of_vsla_groups"
                  name="no_of_vsla_groups"
                  label="No. of VSLA Group"
                  type="number"
                  rules="required|numeric|min_value:0"
              />
            </div>
            <div class="col-12 mt-1">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <h5 class="mb-0">Executives</h5>
                <am-button
                    variant="outline-primary"
                    size="sm"
                    @click="addExecutive"
                >
                  Add Executive
                </am-button>
              </div>
              <div
                  v-for="(executive, index) in organization.executives"
                  :key="`exec-${index}`"
                  class="border rounded p-1 mb-1"
              >
                <div class="row">
                  <div class="col-md-6">
                    <am-input
                        v-model="executive.name"
                        :name="`executive_name_${index}`"
                        label="Name"
                        rules="required"
                    />
                  </div>
                  <div class="col-md-6">
                    <am-input
                        v-model="executive.position"
                        :name="`executive_position_${index}`"
                        label="Position"
                        rules="required"
                    />
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-10">
                    <am-input
                        v-model="executive.contact"
                        :name="`executive_contact_${index}`"
                        label="Contact"
                        rules="required"
                    />
                  </div>
                  <div class="col-md-2 d-flex align-items-end mb-1">
                    <am-button
                        :icon-only="true"
                        icon="TrashIcon"
                        variant="flat-danger"
                        @click="removeExecutive(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </app-collapse-item>
        <app-collapse-item
            id="address-details"
            title="Address Details"
        >
          <div class="row">
            <div class="col-md-6">
              <am-input
                  v-model="organization.district"
                  name="district"
                  label="District"
                  rules="required"
              />
            </div>
            <div class="col-md-6">
              <am-input
                  v-model="organization.community"
                  name="community"
                  label="Community"
                  rules="required"
              />
            </div>
          </div>
        </app-collapse-item>
        <app-collapse-item
            id="contact-details"
            title="Contact Details"
        >
          <div class="row">
            <div class="col-md-6">
              <am-input
                  v-model="organization.contact_person_name"
                  name="contact_person_name"
                  label="Contact Person Name"
                  rules="required"
              />
            </div>
            <div class="col-md-6">
              <am-input
                  v-model="organization.contact_person_phone"
                  name="contact_person_phone"
                  label="Contact Person Phone"
                  rules="required|min_length:10|max_length:15"
                  type="number"
              />
            </div>
            <!-- RENAMED Treasurer Name -->
            <div class="col-md-6">
              <am-input
                  v-model="organization.secondary_contact"
                  name="secondary_contact"
                  label="Secondary Contact"
                  rules="required"
              />
            </div>
            <!-- RENAMED Treasurer Phone -->
            <div class="col-md-6">
              <am-input
                  v-model="organization.secondary_contact_phone"
                  name="secondary_contact_phone"
                  label="Secondary Contact Phone No."
                  rules="required|min_length:10|max_length:15"
                  type="number"
              />
            </div>
            <div class="col-md-6">
              <am-input
                  v-model="organization.email"
                  name="farmer_group_email"
                  label="Society Email"
                  rules="email"
              />
            </div>
          </div>
        </app-collapse-item>
      </app-collapse>
      <!-- Logo upload section -->
      <div class="row mt-2">
        <div class="col-12 mb-1">
          <am-file-button
              label="Upload Logo"
              variant="outline-primary"
              @onFileUpload="handleFileUpload"
          />
        </div>
        <div
            v-if="organizationLogo"
            class="col-12 mt-1 mb-2"
        >
          <label>Logo Preview</label>
          <div class="d-flex align-items-center">
            <am-avatar-data-url
                :size="60"
                :src="organizationLogo"
            />
            <div class="mr-1"/>
            <am-button
                :icon-only="true"
                icon="XIcon"
                variant="flat-danger"
                @click="removeLogo()"
            />
          </div>
        </div>
      </div>
      <!-- Modal actions -->
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
              name="cancel"
              variant="flat-danger"
              icon="XIcon"
              class="mr-1"
              label="Cancel"
              @click="closeOrganizationModal()"
          />
          <am-button
              name="save_organization"
              :label="updateOrganizationMode?'Update':'Create'"
              :disabled="invalid"
              :loading="organizationLoading"
              loading-text="Loading..."
              @click="updateOrganizationMode?updateOrganization():createOrganization()"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import { serialize } from 'object-to-formdata'
import { ValidationObserver } from 'vee-validate'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import logData from '@/libs/log'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmDatePicker from '@/farmbusiness/components/AmDatePicker.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmFileButton from '@/farmbusiness/components/AmFileButton.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmDropdown from '@/farmbusiness/components/AmDropdown.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import AmAvatarDataUrl from '@/farmbusiness/components/AmAvatarDataUrl.vue'

// Define the base structure for the organization object
const org = {
  id: null,
  name: null,
  registration_no: null,
  date_established: null,
  // membership_size_id: null, // REMOVED
  date_joined_union: null, // RENAMED (was date_joined)
  join_reason_id: null,
  // country_id: null, // REMOVED
  // region_id: null, // REMOVED
  district: null,
  community: null,
  contact_person_name: null,
  contact_person_phone: null,
  secondary_contact: null, // RENAMED (was treasurer_name)
  secondary_contact_phone: null, // RENAMED (was treasurer_phone)
  logo: null,
  email: null,
  delete_logo: false,
  sustainability_certification: null, // Holds selected dropdown value or 'Other'
  other_sustainability_certification: null, // ADDED: Holds custom text input value
  annual_revenue: null,
  no_of_vsla_groups: 0,
  executives: [],
}

export default {
  name: 'OrganizationSetup',
  components: {
    AmAvatarDataUrl,
    ErrorDisplay,
    AmModal,
    AmDropdown,
    AmButton,
    AmFileButton,
    AmSelect,
    AmDatePicker,
    AmInput,
    ValidationObserver,
    BRow,
    BDropdownItem,
    BDropdownDivider,
    AppCollapse,
    AppCollapseItem,
  },
  mixins: [showToast],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    id: {
      type: [Number, String, null], // Allow different ID types
      default: null,
    },
  },
  data() {
    return {
      error: {},
      organizationModalOpened: false,
      updateOrganizationMode: false,
      organizationLoading: false,
      deletingOrganization: false,
      selectedOrganizationId: null,
      organization: { ...org }, // Initialize with base structure
      organizationLogo: null, // Holds URL/DataURL for preview

      // --- Dropdown Options ---
      sustainabilityCertificationOptions: [
        'Fairtrade',
        'ISO 22000 & HACCP',
        'Cocoa life',
        'Organic certificate',
        'Rainforest alliance (RA)',
        'Mondelez international',
        'GHAFFAP',
        'Other' // The value that triggers the conditional input
      ],
      annualRevenueOptions: [
        '<10k',
        '>10k but <50k',
        '>50k <100k',
        '>100k'
      ],
    }
  },
  computed: {
    // Get the full object for the selected ID
    selectedOrganization() {
      if (!this.selectedOrganizationId) return null;
      const organizationsList = this.organizations || [];
      // Ensure consistent ID comparison (e.g., string vs number)
      const org = organizationsList.find(o => String(o.id) === String(this.selectedOrganizationId));
      return org ? { ...org } : null; // Return a copy or null
    },
    organizations() {
      return this.$store.getters['organization/organizations'] || []
    },
    joinReasons() {
      return this.$store.getters['organization/joinReasons'] || []
    },
    // Computed property to control visibility of the 'Other' input field
    showOtherSustainabilityInput() {
      return this.organization.sustainability_certification === 'Other';
    }
  },
  watch: {
    // Watcher to clear 'other' sustainability field when main selection changes away from 'Other'
    'organization.sustainability_certification'(newValue) {
      if (newValue !== 'Other') {
        this.organization.other_sustainability_certification = null;
      }
    },
    modalOpened(opened) {
      this.organizationModalOpened = opened
      if (opened && !this.id) {
        this.resetOrganizationForm();
      }
      if (opened && this.id) {
        this.selectedOrganizationId = this.id;
        this.populateOrganizationForm();
      }
    },
    id(newId) {
      // Ensure consistent ID type if necessary
      this.selectedOrganizationId = newId ? String(newId) : null;
      if (this.organizationModalOpened) {
        if (newId) {
          this.populateOrganizationForm();
        } else {
          this.resetOrganizationForm();
        }
      }
    },
  },
  methods: {
    handleFileUpload(upload) {
      this.organizationLogo = upload.dataUrl // For preview
      this.organization.logo = upload.file // Actual file for submission
      this.organization.delete_logo = false // Reset delete flag if new logo uploaded
    },
    removeLogo() {
      this.organization.logo = null // Remove file object
      this.organization.delete_logo = true // Flag for backend to delete existing logo
      this.organizationLogo = null // Clear preview
    },
    resetOrganizationForm() {
      this.organization = { ...org, executives: [] } // Reset to base structure
      this.selectedOrganizationId = null
      this.updateOrganizationMode = false
      this.organizationLogo = null
      this.error = {} // Clear errors
      // Reset validation state
      this.$nextTick(() => {
        if (this.$refs.organizationForm) {
          this.$refs.organizationForm.reset()
        }
      })
    },
    addExecutive() {
      if (!Array.isArray(this.organization.executives)) {
        this.$set(this.organization, 'executives', [])
      }
      this.organization.executives.push({
        id: null,
        name: null,
        position: null,
        contact: null,
      })
    },
    removeExecutive(index) {
      const executive = this.organization.executives[index]
      if (executive && executive.id && this.organization.id) {
        this.$store.dispatch('organization/deleteExecutive', {
          organizationId: this.organization.id,
          executiveId: executive.id,
        }).then(() => {
          this.organization.executives.splice(index, 1)
          this.showSuccess('Executive removed')
        }).catch(() => {
          this.showError('Failed to remove executive')
        })
        return
      }
      this.organization.executives.splice(index, 1)
    },
    // Populate form based on the computed selectedOrganization
    populateOrganizationForm() {
      // Ensure we use the computed property which finds the org by selectedOrganizationId
      const currentSelectedOrg = this.selectedOrganization;

      if (currentSelectedOrg) {
        this.updateOrganizationMode = true
        // Map data, including renamed fields
        this.organization = {
          ...org, // Start with clean structure
          // Spread the *actual* fields from the fetched organization data
          ...currentSelectedOrg,
          // Explicitly map renamed fields FROM the source (currentSelectedOrg)
          secondary_contact: currentSelectedOrg.treasurer_name,
          secondary_contact_phone: currentSelectedOrg.treasurer_phone,
          date_joined_union: currentSelectedOrg.date_joined,
          no_of_vsla_groups: currentSelectedOrg.no_of_vsla_groups ?? 0,
          executives: (currentSelectedOrg.executives || []).map(e => ({
            id: e.id,
            name: e.name,
            position: e.position,
            contact: e.contact,
          })),
          // Ensure fields removed from the form/org object are not included
          membership_size_id: undefined,
          country_id: undefined,
          region_id: undefined,
          // Ensure treasurer fields aren't submitted if they exist in currentSelectedOrg
          treasurer_name: undefined,
          treasurer_phone: undefined,
          // Handle logo separately
          logo: null, // Don't copy the file object/url itself to the form model's logo property
          delete_logo: false, // Reset delete flag when populating
        }
        // Set the preview URL
        this.organizationLogo = currentSelectedOrg.logo // Assuming logo URL is in this property
      } else {
        // If no organization is found for the ID, reset the form
        this.resetOrganizationForm()
      }
      // Reset validation state after population
      this.$nextTick(() => {
        if (this.$refs.organizationForm) {
          this.$refs.organizationForm.reset();
        }
      });
    },
    closeOrganizationModal() {
      this.organizationModalOpened = false
      this.$emit('modalClosed')
      this.resetOrganizationForm(); // Also reset form on close
    },
    // Prepare data for submission, mapping frontend names back to backend if needed
    preparePayload() {
      const payload = { ...this.organization };

      // Map renamed fields back to backend expectations
      payload.date_joined = payload.date_joined_union;
      payload.treasurer_name = payload.secondary_contact;
      payload.treasurer_phone = payload.secondary_contact_phone;

      // Remove frontend-only or adjusted fields before sending
      delete payload.date_joined_union;
      delete payload.secondary_contact;
      delete payload.secondary_contact_phone;

      // Ensure logo file object is present or null, not the preview URL
      if (payload.logo instanceof File) {
        // Keep the file object
      } else {
        // If it's not a file (e.g., null or wasn't changed), ensure it's deleted or handled correctly
        // If logo wasn't changed and wasn't removed, don't send the logo field
        if (!payload.delete_logo && !(payload.logo instanceof File)) {
          delete payload.logo;
        }
      }

      // Remove fields that were dropped from the form but might exist in the object
      delete payload.membership_size_id;
      delete payload.country_id;
      delete payload.region_id;

      return serialize(payload, { indices: true, booleansAsIntegers: true }); // Use serialization
    },
    createOrganization() {
      this.error = {}
      this.organizationLoading = true
      const payload = this.preparePayload();

      this.$store.dispatch('organization/createOrganization', payload)
          .then(res => {
            this.showSuccess('Created Society successfully')
            this.resetOrganizationForm()
            this.$emit('created'); // Emit event on success
          })
          .catch(err => {
            logData(err)
            this.error = err.errors || { detail: err.message || 'An unknown error occurred.' } // Adjust error handling
            this.showError('Failed to create Society')
          })
          .finally(() => {
            this.organizationLoading = false
          })
    },
    updateOrganization() {
      this.error = {}
      if (!this.organization.id) {
        this.showError('Cannot update: Society ID is missing.');
        return;
      }
      this.organizationLoading = true

      const payload = this.preparePayload();

      this.$store.dispatch('organization/updateOrganization', {
        organization: payload, // Send serialized data
        id: this.organization.id
      })
          .then(res => {
            this.showSuccess('Updated Society successfully')
            this.$emit('updated'); // Emit event on success
            // Optionally re-fetch or update local data if needed after update
          })
          .catch(err => {
            logData(err)
            this.error = err.errors || { detail: err.message || 'An unknown error occurred.' } // Adjust error handling
            this.showError('Failed to update Society')
          })
          .finally(() => {
            this.organizationLoading = false
          })
    },
    deleteOrganization() {
      if (!this.selectedOrganizationId) {
        this.showInfo('Please select a Society to delete')
        return
      }
      this.deletingOrganization = true
      this.$store.dispatch('organization/deleteOrganization', this.selectedOrganizationId)
          .then(() => {
            this.showSuccess('Deleted Successfully')
            this.resetOrganizationForm()
            this.$emit('deleted'); // Emit event on success
          })
          .catch((err) => {
            logData(err)
            this.showError('Failed to delete Society')
            this.error = { detail: err.message || 'Could not delete the selected society.' }
          })
          .finally(() => {
            this.deletingOrganization = false
          })
    },
  }
}
</script>

<style scoped>
/* Add any specific styles if needed */
</style>